// src/components/orders/OrderProducts.jsx
import React from "react";
import Barcode from "react-barcode";
import {
  useAddProduct,
  useDeleteProduct,
  useTogglePacked,
  useUpdateProduct,
  useSetDamage,
  useSetSorry,
} from "@queries/order";
import { useAuth } from "@context/AuthContext";
import AddProductForm from "./AddProductForm";
import ImageUploader from "@components/upload/ImageUploader";
import config from "@config/config";

const imageBaseUrl = (config.imageBaseUrl || "").replace(/\/+$/, "") + "/";

export default function OrderProducts({ orderId, products }) {
  const auth = useAuth();
  const canUpdateDirectly = auth?.hasPermission?.(
    "sale/can_update_order_product_directly"
  );

  const [editMode, setEditMode] = React.useState(false);
  const isEditing = canUpdateDirectly && editMode;

  const [openFlagFormId, setOpenFlagFormId] = React.useState(null);
  const [flagDrafts, setFlagDrafts] = React.useState({});

  const add = useAddProduct(orderId);
  const del = useDeleteProduct(orderId);
  const upd = useUpdateProduct(orderId);
  const togglePacked = useTogglePacked(orderId);

  const setDamage = useSetDamage(orderId);
  const setSorry = useSetSorry(orderId);

  const [packDrafts, setPackDrafts] = React.useState({}); // { [order_product_id]: { value: "", error: "" } }

  const onAdd = (payload) => {
    if (!isEditing) return;
    add.mutate(payload);
  };

  const getDraft = (p) => {
    const existing = flagDrafts[p.order_product_id];
    if (existing) return existing;
    return {
      is_damaged: !!p.is_damaged,
      damage_note: p.damage_note || "",
      damage_image: p.damage_image || "",
      is_sorry: !!p.is_sorry,
      sorry_note: p.sorry_note || "",
      sorry_image: p.sorry_image || "",
    };
  };

  const setDraft = (orderProductId, patch) => {
    setFlagDrafts((prev) => ({
      ...prev,
      [orderProductId]: { ...(prev[orderProductId] || {}), ...patch },
    }));
  };

  const openFlags = (p) => {
    setOpenFlagFormId((curr) => {
      const next = curr === p.order_product_id ? null : p.order_product_id;
      if (next) {
        const d = getDraft(p);
        setFlagDrafts((prev) => ({ ...prev, [p.order_product_id]: d }));
      }
      return next;
    });
  };

  const saveFlags = async (p) => {
    const d = getDraft(p);

    await Promise.all([
      setDamage.mutateAsync({
        order_product_id: p.order_product_id,
        payload: {
          is_damaged: d.is_damaged ? 1 : 0,
          damage_note: d.damage_note || null,
          damage_image: d.damage_image || null,
        },
      }),
      setSorry.mutateAsync({
        order_product_id: p.order_product_id,
        payload: {
          is_sorry: d.is_sorry ? 1 : 0,
          sorry_note: d.sorry_note || null,
          sorry_image: d.sorry_image || null,
        },
      }),
    ]);

    setOpenFlagFormId(null);
  };

  const getUnpackedCount = (p) => Number(p.not_packed_count ?? 0) || 0;

  const setUnpackedCount = (p, nextCount) => {
    const count = Math.max(0, Number(nextCount || 0));
    togglePacked.mutate({
      order_product_id: p.order_product_id,
      packed: count === 0,
      count,
    });
  };

  const getPackDraft = (orderProductId) =>
    packDrafts[orderProductId] || { value: "", error: "" };

  const setPackDraft = (orderProductId, patch) => {
    setPackDrafts((prev) => ({
      ...prev,
      [orderProductId]: {
        ...(prev[orderProductId] || { value: "", error: "" }),
        ...patch,
      },
    }));
  };

  const applyPackChange = (p, mode) => {
    const unpackedCount = getUnpackedCount(p);
    const d = getPackDraft(p.order_product_id);

    const raw = String(d.value ?? "").trim();
    const n = Number(raw);

    if (!raw || !Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
      setPackDraft(p.order_product_id, {
        error: "Enter a positive whole number.",
      });
      return;
    }

    if (mode === "pack") {
      if (n > unpackedCount) {
        setPackDraft(p.order_product_id, {
          error: "Packed count cannot be more than current unpacked count.",
        });
        return;
      }
      const next = unpackedCount - n;
      if (next < 0) {
        setPackDraft(p.order_product_id, {
          error: "Unpacked count cannot go under zero.",
        });
        return;
      }
      setPackDraft(p.order_product_id, { error: "" });
      setUnpackedCount(p, next);
      setPackDraft(p.order_product_id, { value: "", error: "" });
      return;
    }

    if (mode === "unpack") {
      const next = unpackedCount + n;
      if (next < 0) {
        setPackDraft(p.order_product_id, {
          error: "Unpacked count cannot go under zero.",
        });
        return;
      }
      setPackDraft(p.order_product_id, { error: "" });
      setUnpackedCount(p, next);
      setPackDraft(p.order_product_id, { value: "", error: "" });
    }
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 20 }}>
      <div
        className="toolbar"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <strong>Products</strong>
          <span style={{ fontSize: 12, color: "#777" }}>
            {products.length} item{products.length === 1 ? "" : "s"}
          </span>
        </div>

        {canUpdateDirectly && (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setEditMode((v) => !v)}
          >
            {isEditing ? "Hide update controls" : "Show update controls"}
          </button>
        )}
      </div>

      <div
        className="table-wrap"
        style={{
          padding: 8,
          borderRadius: 6,
          border: "1px solid #e3e3e3",
          background: "#fff",
          marginTop: 4,
        }}
      >
        <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ padding: "8px 6px" }}>Product</th>
              <th style={{ padding: "8px 6px" }}>Model</th>
              <th style={{ padding: "8px 6px" }}></th>
              <th style={{ padding: "8px 6px" }}>Unpacked</th>
              <th style={{ padding: "8px 6px" }}>Barcode</th>
              <th style={{ padding: "8px 6px" }}>Qty</th>
              <th style={{ padding: "8px 6px" }}>Unit Price</th>
              <th style={{ padding: "8px 6px" }}>Total</th>
              <th style={{ padding: "8px 6px", width: 220 }}>Damage</th>
              <th style={{ padding: "8px 6px", width: 120 }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {products.map((p) => {
              const unpackedCount = getUnpackedCount(p);
              const rowIsRed = unpackedCount > 0;
              const rowIsGreen = unpackedCount === 0;
              const rowBg = rowIsRed ? "#fde2e2" : rowIsGreen ? "#dcfce7" : undefined;

              const flagged = !!p.is_damaged || !!p.is_sorry;
              const isOpen = openFlagFormId === p.order_product_id;
              const d = getDraft(p);

              const packD = getPackDraft(p.order_product_id);

              const tdStyle = (extra) => ({
                padding: "6px 6px",
                backgroundColor: rowBg,
                ...extra,
              });

              return (
                <tr key={p.order_product_id}>
                  <td style={tdStyle()}>
                    {!!p.is_damaged && (
                      <div style={{ color: "#b91c1c", fontWeight: 700, fontSize: 12 }}>
                        Damaged Item
                      </div>
                    )}
                    {!!p.is_sorry && (
                      <div style={{ color: "#15803d", fontWeight: 700, fontSize: 12 }}>
                        Sorry Flagged
                      </div>
                    )}

                    <div>{p.name}</div>

                    {Array.isArray(p.options) &&
                      p.options.map((o) => (
                        <div key={o.name} style={{ fontSize: 12, color: "#666" }}>
                          – {o.name}: {o.value}
                        </div>
                      ))}
                  </td>

                  <td style={tdStyle()}>{p.model}</td>

                  <td style={tdStyle()}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          className="input"
                          type="number"
                          min={1}
                          step={1}
                          placeholder="0"
                          style={{ width: 90 }}
                          value={packD.value}
                          onChange={(e) =>
                            setPackDraft(p.order_product_id, {
                              value: e.target.value,
                              error: "",
                            })
                          }
                        />

                        <button
                          type="button"
                          className="btn btn-sm btn-default"
                          onClick={() => applyPackChange(p, "unpack")}
                        >
                          un packed
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm btn-default"
                          onClick={() => applyPackChange(p, "pack")}
                          disabled={unpackedCount <= 0}
                        >
                          packed
                        </button>
                      </div>

                      {!!packD.error && (
                        <div style={{ fontSize: 12, color: "#b91c1c", fontWeight: 600 }}>
                          {packD.error}
                        </div>
                      )}
                    </div>
                  </td>

                  <td style={tdStyle()}>
                    <span
                      className="mono"
                      style={{
                        fontSize: 12,
                        color: unpackedCount > 0 ? "#b91c1c" : "#374151",
                        fontWeight: 700,
                      }}
                    >
                      {unpackedCount}
                    </span>
                  </td>

                  <td className="mono" style={tdStyle()}>
                    {p.barcode && (
                      <Barcode
                        value={String(p.barcode)}
                        height={40}
                        fontSize={10}
                        displayValue={true}
                      />
                    )}

                    <a
                      href={p.barcode_label}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-info"
                      style={{ marginLeft: 8 }}
                    >
                      <i className="fa fa-barcode" aria-hidden="true"></i>
                    </a>

                    <a
                      href={p.print_label}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-info"
                      style={{ marginLeft: 8 }}
                    >
                      <i className="fa fa-address-card" aria-hidden="true"></i>
                    </a>
                  </td>

                  <td style={tdStyle()}>
                    {isEditing ? (
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <input
                          id={`qty-${p.order_product_id}`}
                          className="input"
                          type="number"
                          min={1}
                          defaultValue={p.quantity}
                          style={{ width: 80 }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-info"
                          onClick={() => {
                            if (!isEditing) return;
                            const el = document.getElementById(`qty-${p.order_product_id}`);
                            if (!el) return;
                            const q = Number(el.value || p.quantity);
                            if (q !== p.quantity) {
                              upd.mutate({
                                order_product_id: p.order_product_id,
                                quantity: q,
                              });
                            }
                          }}
                        >
                          <i className="fa fa-refresh" aria-hidden="true"></i>
                        </button>
                      </div>
                    ) : (
                      <span className="mono">{p.quantity}</span>
                    )}
                  </td>

                  <td className="mono" style={tdStyle()}>
                    {Number(p.price).toFixed(2)}
                  </td>

                  <td className="mono" style={tdStyle()}>
                    {Number(p.total).toFixed(2)}
                  </td>

                  <td style={tdStyle({ width: 220 })}>
                    <button
                      type="button"
                      className={`btn btn-sm ${flagged ? "btn-danger" : "btn-success"}`}
                      onClick={() => openFlags(p)}
                      style={{ width: "100%" }}
                    >
                      Damage and Sorry
                    </button>

                    {isOpen && (
                      <div
                        style={{
                          marginTop: 8,
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                          background: "#fff",
                          padding: 10,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            marginBottom: 8,
                            color: "#166534",
                          }}
                        >
                          Damage and Sorry
                        </div>

                        <div style={{ marginBottom: 10 }}>
                          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input
                              type="checkbox"
                              checked={!!d.is_damaged}
                              onChange={(e) =>
                                setDraft(p.order_product_id, {
                                  is_damaged: e.target.checked,
                                  ...(e.target.checked
                                    ? {}
                                    : { damage_note: "", damage_image: "" }),
                                })
                              }
                            />
                            <span>Is Damaged Flagged</span>
                          </label>

                          <textarea
                            className="oc-input"
                            rows={3}
                            style={{ marginTop: 8 }}
                            value={d.damage_note}
                            onChange={(e) =>
                              setDraft(p.order_product_id, { damage_note: e.target.value })
                            }
                            disabled={!d.is_damaged}
                            placeholder="damage note"
                          />

                          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                            <input
                              className="oc-input"
                              value={d.damage_image}
                              onChange={(e) =>
                                setDraft(p.order_product_id, { damage_image: e.target.value })
                              }
                              disabled={!d.is_damaged}
                              placeholder="damage image path"
                            />
                            <ImageUploader
                              value={d.damage_image}
                              onChange={(newPath) => {
                                if (newPath) {
                                  setDraft(p.order_product_id, { damage_image: newPath });
                                }
                              }}
                            />
                          </div>

                          {d.damage_image && (
                            <div style={{ marginTop: 8 }}>
                              <img
                                src={imageBaseUrl + d.damage_image}
                                alt=""
                                style={{
                                  width: 70,
                                  height: 70,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  border: "1px solid #e5e7eb",
                                }}
                              />
                            </div>
                          )}
                        </div>

                        <div style={{ borderTop: "1px solid #eee", paddingTop: 10 }}>
                          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input
                              type="checkbox"
                              checked={!!d.is_sorry}
                              onChange={(e) =>
                                setDraft(p.order_product_id, {
                                  is_sorry: e.target.checked,
                                  ...(e.target.checked ? {} : { sorry_note: "", sorry_image: "" }),
                                })
                              }
                            />
                            <span>Is Sorry Flagged</span>
                          </label>

                          <textarea
                            className="oc-input"
                            rows={3}
                            style={{ marginTop: 8 }}
                            value={d.sorry_note}
                            onChange={(e) =>
                              setDraft(p.order_product_id, { sorry_note: e.target.value })
                            }
                            disabled={!d.is_sorry}
                            placeholder="sorry note"
                          />

                          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                            <input
                              className="oc-input"
                              value={d.sorry_image}
                              onChange={(e) =>
                                setDraft(p.order_product_id, { sorry_image: e.target.value })
                              }
                              disabled={!d.is_sorry}
                              placeholder="sorry image path"
                            />
                            <ImageUploader
                              value={d.sorry_image}
                              onChange={(newPath) => {
                                if (newPath) {
                                  setDraft(p.order_product_id, { sorry_image: newPath });
                                }
                              }}
                            />
                          </div>

                          {d.sorry_image && (
                            <div style={{ marginTop: 8 }}>
                              <img
                                src={imageBaseUrl + d.sorry_image}
                                alt=""
                                style={{
                                  width: 70,
                                  height: 70,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  border: "1px solid #e5e7eb",
                                }}
                              />
                            </div>
                          )}
                        </div>

                        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-default"
                            onClick={() => setOpenFlagFormId(null)}
                            style={{ flex: 1 }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => saveFlags(p)}
                            disabled={setDamage.isLoading || setSorry.isLoading}
                            style={{ flex: 1 }}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    )}
                  </td>

                  <td style={tdStyle({ width: 120 })}>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      {isEditing && (
                        <button
                          className="icon-btn btn btn-sm btn-danger"
                          title="Delete"
                          onClick={() => del.mutate(p.order_product_id)}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}

            {products.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: "center", padding: 24 }}>
                  No products
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canUpdateDirectly && isEditing && (
        <div
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: "1px solid #eee",
            display: "block",
            justifyContent: "flex-end",
          }}
        >
          <div
            style={{
              flex: "0 0 380px",
              background: "#fafafa",
              border: "1px solid #e3e3e3",
              borderRadius: 6,
              padding: 12,
            }}
          >
            <div
              style={{
                marginBottom: 8,
                fontSize: 13,
                fontWeight: 600,
                borderBottom: "1px solid #ddd",
                paddingBottom: 4,
              }}
            >
              Add product
            </div>
            <AddProductForm onAdd={onAdd} />
          </div>
        </div>
      )}
    </div>
  );
}

